import { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { Badge, Button, Dialog, Pane, Position, SelectField, SelectMenu, Textarea, TextInputField, toaster } from 'evergreen-ui';
import DataTable from '../../common/DataTable/DataTable';
import { getInterestRegistrations, getSelectedEmails, runLottery, updateStatus } from '../actions/InterestRegistrationActions';

const statusOptions = [
    { label: 'În așteptare', value: 'pending' },
    { label: 'Selectat', value: 'selected' },
    { label: 'Notificat', value: 'notified' },
    { label: 'Comandat', value: 'ordered' },
    { label: 'Anulat', value: 'cancelled' }
];

const statusColors = {
    'pending': 'neutral',
    'selected': 'blue',
    'notified': 'orange',
    'ordered': 'green',
    'cancelled': 'red'
};

const statusLabels = {
    'pending': 'În așteptare',
    'selected': 'Selectat',
    'notified': 'Notificat',
    'ordered': 'Comandat',
    'cancelled': 'Anulat'
};

class ListInterestRegistrations extends Component {
    state = {
        campaignFilter: '',
        statusFilter: '',
        // Lottery dialog state
        showLotteryDialog: false,
        lotteryCount: 1,
        lotteryLoading: false,
        // Selected emails dialog state
        showEmailsDialog: false,
        selectedEmails: [],
        emailsLoading: false
    }

    handleTableChange = (opts) => {
        const extraFilters = {};
        if (this.state.campaignFilter) {
            extraFilters.campaignId = this.state.campaignFilter;
        }
        if (this.state.statusFilter) {
            extraFilters.status = this.state.statusFilter;
        }
        this.props.dispatch(getInterestRegistrations({ ...opts, extraFilters }));
    }

    handleFilterChange = (field, value) => {
        this.setState({ [field]: value }, () => {
            this.handleTableChange({ start: 0, limit: 20 });
        });
    }

    handleRunLottery = async () => {
        if (!this.state.campaignFilter) {
            toaster.warning('Selectează o campanie pentru a rula loteria');
            return;
        }

        this.setState({ lotteryLoading: true });
        try {
            const result = await runLottery(this.state.campaignFilter, parseInt(this.state.lotteryCount));
            toaster.success(
                `Loterie finalizată: ${result.data.selectedCount} selectați, ${result.data.rejectedCount} respinși`
            );
            this.setState({ showLotteryDialog: false, lotteryCount: 1 });
            // Refresh the table
            this.handleTableChange({ start: 0, limit: 20 });
        } catch (error) {
            toaster.danger(`Eroare la rularea loteriei: ${error.message || error}`);
        } finally {
            this.setState({ lotteryLoading: false });
        }
    }

    handleShowSelectedEmails = async () => {
        if (!this.state.campaignFilter) {
            toaster.warning('Selectează o campanie pentru a vedea emailurile selectate');
            return;
        }

        this.setState({ emailsLoading: true, showEmailsDialog: true });
        try {
            const result = await getSelectedEmails(this.state.campaignFilter);
            this.setState({ selectedEmails: result.data.emails || [] });
        } catch (error) {
            toaster.danger(`Eroare la încărcarea emailurilor: ${error.message || error}`);
        } finally {
            this.setState({ emailsLoading: false });
        }
    }

    copyEmailsToClipboard = () => {
        const emailText = this.state.selectedEmails.map(e => e.email).join('\n');
        navigator.clipboard.writeText(emailText).then(() => {
            toaster.success('Emailurile au fost copiate în clipboard');
        });
    }

    handleStatusChange = async (registrationId, newStatus) => {
        try {
            await this.props.dispatch(updateStatus(registrationId, newStatus));
            toaster.success('Statusul a fost actualizat');
            // Refresh the table
            this.handleTableChange({ start: 0, limit: 20 });
        } catch (error) {
            toaster.danger(`Eroare la actualizarea statusului: ${error.message || error}`);
        }
    }

    render() {
        return (<>
            <Pane display="flex" marginBottom={16} gap={16} alignItems="flex-end">
                <SelectField
                    label="Campanie"
                    value={this.state.campaignFilter}
                    onChange={e => this.handleFilterChange('campaignFilter', e.target.value)}
                    width={200}
                >
                    <option value="">Toate campaniile</option>
                    <option value="moltres-upc-2026">Moltres UPC 2026</option>
                </SelectField>

                <SelectField
                    label="Status"
                    value={this.state.statusFilter}
                    onChange={e => this.handleFilterChange('statusFilter', e.target.value)}
                    width={200}
                >
                    <option value="">Toate statusurile</option>
                    <option value="pending">În așteptare</option>
                    <option value="selected">Selectat</option>
                    <option value="notified">Notificat</option>
                    <option value="ordered">Comandat</option>
                    <option value="cancelled">Anulat</option>
                </SelectField>

                <Pane display="flex" gap={8} marginBottom={4}>
                    <Button
                        appearance="primary"
                        intent="success"
                        disabled={!this.state.campaignFilter}
                        onClick={() => this.setState({ showLotteryDialog: true })}
                    >
                        Rulează Loterie
                    </Button>
                    <Button
                        appearance="primary"
                        disabled={!this.state.campaignFilter}
                        onClick={this.handleShowSelectedEmails}
                    >
                        Vezi Emailuri Selectate
                    </Button>
                </Pane>
            </Pane>

            {/* Lottery Dialog */}
            <Dialog
                isShown={this.state.showLotteryDialog}
                title="Rulează Loterie"
                onCloseComplete={() => this.setState({ showLotteryDialog: false })}
                confirmLabel="Rulează"
                isConfirmLoading={this.state.lotteryLoading}
                onConfirm={this.handleRunLottery}
            >
                <Pane>
                    <p>Campanie: <strong>{this.state.campaignFilter}</strong></p>
                    <p>Aceasta va selecta aleatoriu un număr de înregistrări din cele în așteptare și va respinge restul.</p>
                    <TextInputField
                        label="Câți să fie selectați?"
                        type="number"
                        min={1}
                        value={this.state.lotteryCount}
                        onChange={e => this.setState({ lotteryCount: e.target.value })}
                    />
                </Pane>
            </Dialog>

            {/* Selected Emails Dialog */}
            <Dialog
                isShown={this.state.showEmailsDialog}
                title={`Emailuri Selectate (${this.state.selectedEmails.length})`}
                onCloseComplete={() => this.setState({ showEmailsDialog: false, selectedEmails: [] })}
                hasFooter={false}
                width={600}
            >
                <Pane>
                    {this.state.emailsLoading ? (
                        <p>Se încarcă...</p>
                    ) : this.state.selectedEmails.length === 0 ? (
                        <p>Nu există emailuri selectate pentru această campanie.</p>
                    ) : (
                        <>
                            <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={8}>
                                <span>{this.state.selectedEmails.length} emailuri</span>
                                <Button iconBefore="duplicate" onClick={this.copyEmailsToClipboard}>
                                    Copiază Emailuri
                                </Button>
                            </Pane>
                            <Textarea
                                readOnly
                                value={this.state.selectedEmails.map(e => `${e.name} <${e.email}>`).join('\n')}
                                height={300}
                            />
                        </>
                    )}
                </Pane>
            </Dialog>

            <DataTable
                defaultFilter='id'
                defaultDirection='desc'
                columns={[
                    { id: 'id', label: 'Id' },
                    {
                        id: 'user',
                        label: 'Client',
                        decorator: (item) => {
                            if (item.user?.id) {
                                return (
                                    <Link to={`/users/${item.user.id}`} style={{ color: '#1070ca', textDecoration: 'none' }}>
                                        {item.user.name || 'N/A'}
                                    </Link>
                                );
                            }
                            return item.user?.name || 'N/A';
                        }
                    },
                    {
                        id: 'userEmail',
                        label: 'Email',
                        decorator: (item) => item.user?.email || 'N/A'
                    },
                    {
                        id: 'userPhone',
                        label: 'Telefon',
                        decorator: (item) => item.user?.phone || 'N/A'
                    },
                    { id: 'campaignId', label: 'Campanie' },
                    {
                        id: 'status',
                        label: 'Status',
                        decorator: (item) => (
                            <SelectMenu
                                title="Schimbă Status"
                                options={statusOptions}
                                selected={item.status}
                                position={Position.BOTTOM_LEFT}
                                onSelect={(selected) => this.handleStatusChange(item.id, selected.value)}
                                closeOnSelect={true}
                            >
                                <Badge
                                    color={statusColors[item.status] || 'neutral'}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {statusLabels[item.status] || item.status} ▾
                                </Badge>
                            </SelectMenu>
                        )
                    },
                    { id: 'createdAt', label: 'Înregistrat', decorator: 'datetime' },
                    { id: 'updatedAt', label: 'Modificat', decorator: 'datetime' }
                ]}

                data={this.props.data}
                updateKey={this.props.data}
                page={this.props.page}
                pageSize={this.props.pageSize}
                pageCount={this.props.pageCount}

                onTableChange={this.handleTableChange}

                itemMenu={[
                    { getLink: (item) => `/interest-registrations/${item[0]}`, label: 'Detalii', icon: 'eye-open' },
                    { getLink: (item) => `/interest-registrations/${item[0]}/edit`, label: 'Editează', icon: 'edit' }
                ]}
            />
        </>)
    }
}

function mapStateToProps(state) {
    return state.interestRegistrations;
}

export default connect(mapStateToProps)(ListInterestRegistrations);
